import React, { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Table } from 'antd';
import {
  AppButton,
  AppCheckbox,
  AppInputNumber,
  AppPagination,
  AppSpin,
  AppTable,
  AppTableActionButton,
  AppTooltip,
} from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import type { ContinuationListRow } from '@/services/main/continuation/continuationService';
import { toNumber } from '../utils';

interface Props {
  list: ContinuationListRow[];
  /** R2 drops the A2-only 初検 column. */
  isR2: boolean;
  isLoading: boolean;
  selectedSet: Set<number>;
  patientPrices: Record<number, number>;
  tableWrapperRef: (el: HTMLDivElement | null) => void;
  totalMadoguchi: number;
  totalTreatment: number;
  totalPatient: number;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onNavigateTreatment: (patientId: number | string, yearmonth: string) => void;
  onSelectAllToggle: () => void;
  onSelectInvert: () => void;
  onToggleRow: (id: number, checked: boolean) => void;
  onChangePatientPrice: (id: number, value: number | null) => void;
  onBlurPatientPrice: (record: ContinuationListRow) => void;
  onPrintReset: (id: number) => void;
  onOpenDailyReceipt: (record: ContinuationListRow) => void;
}

const InvoiceTable: React.FC<Props> = ({
  list,
  isR2,
  isLoading,
  selectedSet,
  patientPrices,
  tableWrapperRef,
  totalMadoguchi,
  totalTreatment,
  totalPatient,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onNavigateTreatment,
  onSelectAllToggle,
  onSelectInvert,
  onToggleRow,
  onChangePatientPrice,
  onBlurPatientPrice,
  onPrintReset,
  onOpenDailyReceipt,
}) => {
  const columns: ColumnsType<ContinuationListRow> = useMemo(
    () => [
      {
        title: (
          <div className="flex items-center justify-center gap-1">
            <AppButton type="default" size="small" onClick={onSelectAllToggle}>
              全
            </AppButton>
            <AppButton type="default" size="small" onClick={onSelectInvert}>
              反
            </AppButton>
          </div>
        ),
        key: 'selected',
        width: 100,
        align: 'center',
        render: (_: unknown, record: ContinuationListRow) => (
          <AppCheckbox
            checked={selectedSet.has(record.id)}
            onChange={e => onToggleRow(record.id, e.target.checked)}
          />
        ),
      },
      {
        title: '施術日',
        dataIndex: 'visit_datetime_from',
        key: 'visit_datetime_from',
        width: 90,
        render: (val: string | undefined) => {
          if (!val) return '—';
          return val.length >= 10 ? val.slice(5, 10).replace('-', '/') : val;
        },
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 100,
        render: (val: string | number, record: ContinuationListRow) => (
          <PatientEditLink patientId={record.patient_id} value={val} />
        ),
      },
      {
        title: '受診者名漢字',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="line-clamp-2 break-words">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '受診者名仮名',
        dataIndex: 'name_kana',
        key: 'name_kana',
        width: 200,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="line-clamp-2 break-words">{text}</div>
          </AppTooltip>
        ),
      },
      { title: '保険種別', dataIndex: 'insurer_kind_name', key: 'insurer_kind_name', width: 110 },
      {
        title: '負担割合',
        dataIndex: 'patient_cost_rate_str',
        key: 'patient_cost_rate_str',
        width: 70,
      },
      {
        title: '医療助成',
        dataIndex: 'medical_aid_name',
        key: 'medical_aid_name',
        width: 100,
        render: (val?: string) => val || '—',
      },
      // 初検料 exists only in A2; BD SCR-182 has no such column.
      ...(isR2
        ? []
        : [
            {
              title: '初検',
              dataIndex: 'is_initial_inspection_fee_mark',
              key: 'is_initial_inspection_fee_mark',
              width: 55,
            },
          ]),
      {
        title: '窓口料金',
        dataIndex: 'madoguchi_price',
        key: 'madoguchi_price',
        width: 90,
        render: (v: number | undefined) => toNumber(v, 0).toLocaleString(),
      },
      {
        title: '施術料金',
        dataIndex: 'treatment_price',
        key: 'treatment_price',
        width: 90,
        render: (v: number | undefined) => toNumber(v, 0).toLocaleString(),
      },
      {
        title: '領収金額',
        key: 'patient_price',
        width: 110,
        render: (_: unknown, record: ContinuationListRow) => (
          <AppInputNumber
            min={0}
            value={toNumber(patientPrices[record.id], toNumber(record.patient_price, 0))}
            onChange={value => onChangePatientPrice(record.id, value as number | null)}
            onBlur={() => onBlurPatientPrice(record)}
            className="w-full"
            disabled
          />
        ),
      },
      {
        title: '印刷',
        dataIndex: 'print_invoice_mark',
        key: 'print_invoice_mark',
        width: 55,
        render: (val: string | undefined, record: ContinuationListRow) =>
          val != null && val !== '' ? (
            <AppButton type="link" className="h-auto p-0" onClick={() => onPrintReset(record.id)}>
              {val}
            </AppButton>
          ) : null,
      },
      {
        title: '日別領収',
        key: 'daily_receipt',
        className: 'w-0',
        render: (_: unknown, record: ContinuationListRow) => {
          const searchYearmonth =
            record.visit_datetime_from && record.visit_datetime_from.length >= 7
              ? record.visit_datetime_from.slice(0, 7)
              : '';
          const editable = !!searchYearmonth;
          return (
            <AppButton
              type="primary"
              size="small"
              onClick={() => editable && onOpenDailyReceipt(record)}
              disabled={!editable}
            >
              編集
            </AppButton>
          );
        },
      },
      {
        title: '操作',
        key: 'action',
        className: 'w-0',
        render: (_: unknown, record: ContinuationListRow) => {
          const searchYearmonth =
            record.move_to_visit_clinic_month ?? record.visit_datetime_from?.slice(0, 7) ?? '';
          return (
            <AppTableActionButton
              action="edit"
              size="small"
              onClick={() =>
                searchYearmonth && onNavigateTreatment(record.patient_id, searchYearmonth)
              }
              disabled={!searchYearmonth}
            >
              施術
            </AppTableActionButton>
          );
        },
      },
    ],
    [
      isR2,
      selectedSet,
      patientPrices,
      onSelectAllToggle,
      onSelectInvert,
      onToggleRow,
      onChangePatientPrice,
      onBlurPatientPrice,
      onPrintReset,
      onOpenDailyReceipt,
      onNavigateTreatment,
    ]
  );

  // Columns before 窓口料金: 8 in R2, 9 in A2 (the extra 初検 column).
  const leadColSpan = isR2 ? 8 : 9;
  const summary = useMemo(
    () => (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={leadColSpan} className="font-medium text-center">
            合計
          </Table.Summary.Cell>
          <Table.Summary.Cell index={leadColSpan} className="font-medium">
            {totalMadoguchi.toLocaleString()}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={leadColSpan + 1} className="font-medium">
            {totalTreatment.toLocaleString()}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={leadColSpan + 2} className="font-medium">
            {totalPatient.toLocaleString()}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={leadColSpan + 3} colSpan={3} />
        </Table.Summary.Row>
      </Table.Summary>
    ),
    [leadColSpan, totalMadoguchi, totalPatient, totalTreatment]
  );

  return (
    <div
      ref={tableWrapperRef}
      className="flex-1 mb-3 bg-white border border-gray-100 rounded shadow-md"
    >
      <div className="p-4">
        <AppSpin spinning={isLoading}>
          <AppTable<ContinuationListRow>
            rowKey="id"
            columns={columns}
            dataSource={list}
            summary={() => summary}
            recordCount={totalCount}
            onRow={(record: ContinuationListRow) => ({
              className: record.is_report_only ? 'report-only-row' : '',
            })}
          />
          <AppPagination
            className="mt-3"
            current={currentPage}
            total={totalCount}
            pageSize={pageSize}
            onChange={onPageChange}
          />
        </AppSpin>
      </div>
    </div>
  );
};

export default InvoiceTable;
